import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { cn } from "@/lib/cn";

// The four strength/requirement checks shown live under the new-password field.
export const REQUIREMENTS: { key: string; test: (v: string) => boolean }[] = [
  { key: "reset.reqLength", test: (v) => v.length >= 12 },
  { key: "reset.reqUpper", test: (v) => /[A-Z]/.test(v) },
  { key: "reset.reqNumber", test: (v) => /[0-9]/.test(v) },
  { key: "reset.reqSymbol", test: (v) => /[^A-Za-z0-9]/.test(v) },
];

// Strength label + segment/label colour keyed on how many requirements are met.
const STRENGTH = [
  { label: "reset.strengthWeak", text: "text-red-ink", seg: "bg-red" },
  { label: "reset.strengthWeak", text: "text-red-ink", seg: "bg-red" },
  { label: "reset.strengthFair", text: "text-amber-ink", seg: "bg-amber" },
  { label: "reset.strengthGood", text: "text-amber-ink", seg: "bg-amber" },
  { label: "reset.strengthStrong", text: "text-teal-ink", seg: "bg-teal" },
];

// PasswordStrength is the live meter + 2×2 requirements checklist shared by the
// tenant Set Password and platform Set Password screens.
export function PasswordStrength({ password }: { password: string }) {
  const { t } = useTranslation();
  const met = useMemo(() => REQUIREMENTS.map((r) => r.test(password)), [password]);
  const metCount = met.filter(Boolean).length;
  const strength = STRENGTH[metCount];
  return (
    <div className="flex flex-col gap-3.5 rounded-xl border border-edge-2 bg-surface p-4">
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted">{t("reset.strengthLabel")}</span>
          {password && (
            <span className={cn("text-xs font-bold", strength.text)}>{t(strength.label)}</span>
          )}
        </div>
        <div className="flex gap-1.5">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={cn(
                "h-1.25 flex-1 rounded-full",
                password && i < metCount ? strength.seg : "bg-edge-2",
              )}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        {REQUIREMENTS.map((r, i) => (
          <div key={r.key} className="flex items-center gap-2">
            <span
              className={cn(
                "grid size-4.5 shrink-0 place-items-center rounded-full",
                met[i] ? "bg-teal-bg text-teal-ink" : "border border-edge-3 text-transparent",
              )}
            >
              <Check size={11} strokeWidth={3} />
            </span>
            <span className="text-[13px] text-ink-2">{t(r.key)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
